/**
 * One application, only what it is waiting on. The Applications page shows
 * the whole record of every application; by mid-September that was fifty
 * open questions across thirty rows, and the ones that needed a person were
 * hard to find. This page is the other way round: a needs-you row's "Answer
 * what's left" opens it, and it lists just that application's left
 * questions, each with the one thing that clears it -- an answer box (the
 * question bank: saved once, given on every later form that asks it), a
 * link to the Answers page when the answer belongs there, or the plain
 * word that this one is the person's on the form itself. In Auto, every
 * saved answer puts the application back in the queue on its own.
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "./auth";
import { leftForYou, splitLeft, LeftItem, NOT_BANKABLE } from "./Applications";

interface AppLite {
  jobId: string;
  submission: { state: string; failureDetail: string | null; mode: string };
  job: { title: string; employer: string; applyUrl: string | null; closedAt: string | null };
}

/** What clears a left question: a saved answer, an answer on the Answers page, or the person on the form. */
type Clears = "bank" | "answers" | "preferences" | "form";

const ON_ANSWERS = /not in your answers|Add it in JobCork|not answered in your profile|Application answers|isn.t set\b|is not on record|Add your city|Write your city|unless you have confirmed/i;
const ON_PREFERENCES = /not set in Preferences/i;
const ON_THE_FORM = /never guesses|yours to (choose|explain)|never answers .yes.|could not tell which country|form.s own wording|Tick it yourself|Sign it yourself|A consent JobCork|human check|not a robot|captcha/i;

function clears(question: string, why: string | null): Clears {
  const w = why ?? "";
  if (ON_PREFERENCES.test(w)) return "preferences";
  if (ON_ANSWERS.test(w)) return "answers";
  if (ON_THE_FORM.test(w) || NOT_BANKABLE.test(question) || question.length < 4) return "form";
  return "bank";
}

const CLEARS_WORDS: Record<Clears, string> = {
  bank: "Answer it here once; every later form that asks it gets your answer.",
  answers: "The answer belongs on your Answers page — set it there and every form that asks it fills.",
  preferences: "Set on your Preferences page.",
  form: "JobCork never answers this one for you. Open the form: JobCork fills the rest, you answer this and press Submit.",
};

export default function AnswerLeft({ jobId, autoMode, onBack, onGo, onReview }: {
  jobId: string;
  autoMode: "auto" | "review";
  onBack: () => void;
  onGo: (go: string) => void;
  onReview: (jobId: string) => void;
}) {
  const [app, setApp] = useState<AppLite | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [answered, setAnswered] = useState<string[]>([]);
  const [requeue, setRequeue] = useState<{ busy: boolean; note: string | null }>({ busy: false, note: null });

  const load = useCallback(() => {
    setError(null);
    api<{ applications: AppLite[] }>("/api/applications")
      .then((r) => setApp(r.applications.find((a) => a.jobId === jobId) ?? null))
      .catch((e) => setError(e instanceof ApiError ? e.messages.join(" ") : (e as Error).message));
  }, [jobId]);
  useEffect(load, [load]);

  const tryAgain = async (note: string) => {
    setRequeue({ busy: true, note: null });
    try {
      const r = await api<{ state: string; changed: boolean; note?: string }>(`/api/applications/${jobId}/requeue`, { method: "POST", body: JSON.stringify({ note }) });
      setRequeue({ busy: false, note: r.changed ? "Back in Auto's queue. It goes out by Auto's rules, within your daily limit, with a fresh record." : (r.note ?? "Already in the queue.") });
    } catch (e) {
      setRequeue({ busy: false, note: e instanceof ApiError ? e.messages.join(" ") : (e as Error).message });
    }
  };

  const onSaved = (question: string) => {
    setAnswered((a) => (a.includes(question) ? a : [...a, question]));
    if (autoMode === "auto") void tryAgain(`after you answered “${question}”`);
  };

  const head = (
    <header className="bar">
      <div>
        <h1 className="tight">Answer what's left</h1>
        {app && <p className="muted small">{app.job.title} <span className="muted">— {app.job.employer}</span></p>}
      </div>
      <button className="btn ghost small" onClick={onBack}>Back</button>
    </header>
  );

  if (error) return <>{head}<p className="err small">{error}</p><div className="row"><button className="btn" onClick={load}>Try again</button></div></>;
  if (app === undefined) return <>{head}<p className="muted">Loading…</p></>;
  if (app === null) return <>{head}<p className="muted small">That application is not on this track's board. <button className="step-link small" onClick={() => onGo("applications")}>See Applications</button></p></>;

  const items = app.submission.state === "needs_input" ? (leftForYou(app.submission.failureDetail) ?? []) : [];
  if (items.length === 0) {
    return (
      <>
        {head}
        <p className="small">Nothing is waiting on you here{app.submission.state === "needs_input" ? ` — the stop was not a question: ${app.submission.failureDetail ?? ""}` : ""}.</p>
        <div className="row"><button className="btn ghost small" onClick={() => onReview(jobId)}>See the full record</button></div>
      </>
    );
  }

  const parsed = items.map((item) => { const { question, why } = splitLeft(item); return { item, question, why, clears: clears(question, why) }; });
  const bank = parsed.filter((p) => p.clears === "bank");
  const elsewhere = parsed.filter((p) => p.clears === "answers" || p.clears === "preferences");
  const onForm = parsed.filter((p) => p.clears === "form");
  const done = bank.filter((p) => answered.includes(p.question)).length;

  return (
    <>
      {head}
      <p className="muted small">
        {items.length} question{items.length === 1 ? "" : "s"} the form asked that JobCork could not answer from your record
        {bank.length > 0 && <> · {done} of {bank.length} answered here</>}.
        {" "}{app.job.applyUrl && <a className="step-link small" href={app.job.applyUrl} target="_blank" rel="noreferrer">Open the form ↗</a>}
        {" "}<button className="step-link small" onClick={() => onReview(jobId)}>Full record</button>
      </p>

      {bank.length > 0 && (
        <section className="answer-left-group">
          <h2>Answer here</h2>
          <p className="muted small">{CLEARS_WORDS.bank}</p>
          <ul className="left-for-you answer-left-list">
            {bank.map((p) => <LeftItem key={p.question} item={p.item} employer={app.job.employer} jobId={jobId} onSaved={onSaved} autoMode={autoMode} openAtOnce />)}
          </ul>
        </section>
      )}

      {elsewhere.length > 0 && (
        <section className="answer-left-group">
          <h2>Set once, on your pages</h2>
          <ul className="left-for-you answer-left-list">
            {elsewhere.map((p) => (
              <li key={p.question}>
                <strong>{p.question}</strong>{p.why && <span className="muted"> — {p.why}</span>}
                {" "}<button className="step-link small" onClick={() => onGo(p.clears === "preferences" ? "preferences" : "answers")}>{p.clears === "preferences" ? "Preferences →" : "Answers →"}</button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {onForm.length > 0 && (
        <section className="answer-left-group">
          <h2>Yours, on the form</h2>
          <p className="muted small">{CLEARS_WORDS.form}</p>
          <ul className="left-for-you answer-left-list">
            {onForm.map((p) => <li key={p.question}><strong>{p.question}</strong>{p.why && <span className="muted"> — {p.why}</span>}</li>)}
          </ul>
        </section>
      )}

      <div className="row answer-left-foot">
        {autoMode === "auto" ? (
          <>
            <button className="btn small" disabled={requeue.busy} onClick={() => { void tryAgain("from Answer what's left, by you"); }}>{requeue.busy ? "Putting it back…" : "Try it again now"}</button>
            <span className="muted small">Every answer saved above puts it back in Auto's queue on its own; this does the same without waiting.</span>
          </>
        ) : (
          <span className="muted small">Open the form: JobCork fills what it can in your browser, with what you saved here; you answer the rest and press Submit.</span>
        )}
      </div>
      {requeue.note && <p className="small ok">{requeue.note}</p>}
    </>
  );
}
